import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';

// === Interfaces ===

export interface TaxJurisdiction {
  id: string;
  jurisdictionCode: string;
  jurisdictionName: string;
  country: string;
  stateProvince: string | null;
  currencyCode: string | null;
  taxAuthorityName: string | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  remarks: string | null;
}

export interface TaxType {
  id: string;
  typeCode: string;
  typeName: string;
  taxCategory: string;
  isRecoverable: boolean;
  isWithholding: boolean;
  isReverseCharge: boolean;
  isActive: boolean;
  remarks: string | null;
}

export interface TaxCodeGlMapping {
  id: string;
  taxCodeId: string;
  inputTaxAccountCode: string | null;
  outputTaxAccountCode: string | null;
  taxPayableAccountCode: string | null;
  taxReceivableAccountCode: string | null;
  withholdingTaxAccountCode: string | null;
  deferredTaxAccountCode: string | null;
  /** Asset account for tax an AR customer withholds — a reclaimable credit, the opposite nature
   *  of withholdingTaxAccountCode (AP's liability for tax withheld from a supplier). */
  withholdingTaxReceivableAccountCode: string | null;
}

export interface TaxCode {
  id: string;
  jurisdictionId: string;
  taxTypeId: string;
  code: string;
  name: string;
  taxRate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCompound: boolean;
  recoverablePercent: number;
  minThreshold: number | null;
  maxThreshold: number | null;
  roundingMethod: string;
  isInclusive: boolean;
  isActive: boolean;
  remarks: string | null;
  glMapping: TaxCodeGlMapping | null;
}

export interface TaxGroupComponent {
  taxCodeId: string;
  code: string;
  name: string;
  taxRate: number;
  sequence: number;
  isOnBase: boolean;
}

export interface TaxGroup {
  id: string;
  groupCode: string;
  groupName: string;
  isActive: boolean;
  remarks: string | null;
  components: TaxGroupComponent[];
}

export interface WithholdingTaxRule {
  id: string;
  ruleCode: string;
  ruleName: string;
  taxCodeId: string;
  jurisdictionId: string | null;
  appliesTo: string;
  thresholdAmount: number;
  withholdingPercent: number;
  category: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  remarks: string | null;
}

export interface TaxRegistration {
  id: string;
  entityType: string;
  entityId: string;
  jurisdictionId: string;
  registrationNumber: string;
  registrationType: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  validationStatus: string;
  isExempt: boolean;
  exemptionReason: string | null;
  isActive: boolean;
  remarks: string | null;
}

export interface TaxTransaction {
  id: string;
  sourceModule: string;
  sourceDocumentId: string;
  sourceDocumentNo: string;
  transactionType: string;
  transactionDate: string;
  taxCodeId: string;
  jurisdictionId: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  taxableAmount: number;
  taxAmount: number;
  taxAmountFc: number;
  isInputTax: boolean;
  isRecoverable: boolean;
  isWithholding: boolean;
  isReverseCharge: boolean;
  companyId: string;
  remarks: string | null;
}

export interface TaxCalculationRequest {
  baseAmount: number;
  taxCodeIds?: string[];
  taxGroupId?: string;
  isTaxInclusive: boolean;
  currencyCode?: string;
  exchangeRate?: number;
  transactionDate: string;
  jurisdictionId?: string;
}

export interface TaxLineCalculation {
  taxCodeId: string;
  code: string;
  name: string;
  rate: number;
  taxableAmount: number;
  taxAmount: number;
  recoverableAmount: number;
  nonRecoverableAmount: number;
  isWithholding: boolean;
  isReverseCharge: boolean;
}

export interface TaxCalculationResult {
  netAmount: number;
  totalTaxAmount: number;
  grossAmount: number;
  isInclusive: boolean;
  taxLines: TaxLineCalculation[];
  warnings: string[];
}

export interface TaxSummaryReport {
  dateFrom:  string;
  dateTo:    string;
  companyId: string;
  lines:     TaxSummaryReportItem[];
}

export interface TaxSummaryReportItem {
  taxCodeId: string;
  taxCode: string;
  taxName: string;
  jurisdictionName: string;
  totalTaxableAmount: number;
  totalTaxAmount: number;
  totalRecoverableAmount: number;
  totalNonRecoverableAmount: number;
  transactionCount: number;
}

// === API Functions ===

export const taxApi = {
  // Jurisdictions
  getJurisdictions: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<TaxJurisdiction>>>('/tax/jurisdictions', { params }),
  getJurisdictionById: (id: string) =>
    apiClient.get<ApiResponse<TaxJurisdiction>>(`/tax/jurisdictions/${id}`),
  createJurisdiction: (data: Partial<TaxJurisdiction>) =>
    apiClient.post<ApiResponse<TaxJurisdiction>>('/tax/jurisdictions', data),
  updateJurisdiction: (id: string, data: Partial<TaxJurisdiction>) =>
    apiClient.put<ApiResponse<TaxJurisdiction>>(`/tax/jurisdictions/${id}`, data),
  deleteJurisdiction: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/tax/jurisdictions/${id}`),

  // Tax Types
  getTypes: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<TaxType>>>('/tax/types', { params }),
  getTypeById: (id: string) =>
    apiClient.get<ApiResponse<TaxType>>(`/tax/types/${id}`),
  createType: (data: Partial<TaxType>) =>
    apiClient.post<ApiResponse<TaxType>>('/tax/types', data),
  updateType: (id: string, data: Partial<TaxType>) =>
    apiClient.put<ApiResponse<TaxType>>(`/tax/types/${id}`, data),
  deleteType: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/tax/types/${id}`),

  // Tax Codes
  getCodes: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<TaxCode>>>('/tax/codes', { params }),
  getCodeById: (id: string) =>
    apiClient.get<ApiResponse<TaxCode>>(`/tax/codes/${id}`),
  createCode: (data: Partial<TaxCode>) =>
    apiClient.post<ApiResponse<TaxCode>>('/tax/codes', data),
  updateCode: (id: string, data: Partial<TaxCode>) =>
    apiClient.put<ApiResponse<TaxCode>>(`/tax/codes/${id}`, data),
  deleteCode: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/tax/codes/${id}`),
  getGlMapping: (taxCodeId: string) =>
    apiClient.get<ApiResponse<TaxCodeGlMapping>>(`/tax/codes/${taxCodeId}/gl-mapping`),
  updateGlMapping: (taxCodeId: string, data: Partial<TaxCodeGlMapping>) =>
    apiClient.put<ApiResponse<TaxCodeGlMapping>>(`/tax/codes/${taxCodeId}/gl-mapping`, data),

  // Tax Groups
  getGroups: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<TaxGroup>>>('/tax/groups', { params }),
  getGroupById: (id: string) =>
    apiClient.get<ApiResponse<TaxGroup>>(`/tax/groups/${id}`),
  createGroup: (data: Partial<TaxGroup>) =>
    apiClient.post<ApiResponse<TaxGroup>>('/tax/groups', data),
  updateGroup: (id: string, data: Partial<TaxGroup>) =>
    apiClient.put<ApiResponse<TaxGroup>>(`/tax/groups/${id}`, data),
  deleteGroup: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/tax/groups/${id}`),

  // Withholding Tax Rules
  getWithholdingRules: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<WithholdingTaxRule>>>('/tax/withholding-rules', { params }),
  getWithholdingRuleById: (id: string) =>
    apiClient.get<ApiResponse<WithholdingTaxRule>>(`/tax/withholding-rules/${id}`),
  createWithholdingRule: (data: Partial<WithholdingTaxRule>) =>
    apiClient.post<ApiResponse<WithholdingTaxRule>>('/tax/withholding-rules', data),
  updateWithholdingRule: (id: string, data: Partial<WithholdingTaxRule>) =>
    apiClient.put<ApiResponse<WithholdingTaxRule>>(`/tax/withholding-rules/${id}`, data),
  deleteWithholdingRule: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/tax/withholding-rules/${id}`),

  // Tax Registrations
  getRegistrations: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<TaxRegistration>>>('/tax/registrations', { params }),
  getRegistrationById: (id: string) =>
    apiClient.get<ApiResponse<TaxRegistration>>(`/tax/registrations/${id}`),
  createRegistration: (data: Partial<TaxRegistration>) =>
    apiClient.post<ApiResponse<TaxRegistration>>('/tax/registrations', data),
  updateRegistration: (id: string, data: Partial<TaxRegistration>) =>
    apiClient.put<ApiResponse<TaxRegistration>>(`/tax/registrations/${id}`, data),
  deleteRegistration: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/tax/registrations/${id}`),

  // Tax Transactions (Inquiry)
  getTransactions: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<PagedResult<TaxTransaction>>>('/tax/transactions', { params }),
  getTransactionById: (id: string) =>
    apiClient.get<ApiResponse<TaxTransaction>>(`/tax/transactions/${id}`),

  // Calculate
  calculate: (request: TaxCalculationRequest) =>
    apiClient.post<ApiResponse<TaxCalculationResult>>('/tax/calculate', request),

  // Reports
  /**
   * GET /api/v1/tax/reports/tax-summary
   *
   * The path was /reports/tax-summary, which is not a route this API has — the screen 404'd on
   * every run and showed nothing, which reads exactly like "there is no tax data". There is:
   * the report groups the tax transactions by code and jurisdiction and returns them under
   * `lines`, not as a bare array.
   */
  getTaxSummaryReport: (params?: Record<string, unknown>) =>
    apiClient.get<ApiResponse<TaxSummaryReport>>('/tax/reports/tax-summary', { params }),
};
